import Fuse from 'fuse.js'
import uniqueId from 'lodash/uniqueId'
import {
  bool,
  string,
  func,
  array,
  number,
  oneOf,
  oneOfType,
  object,
} from 'prop-types'
import { useEffect, useReducer, useRef } from 'react'
import { AnalyticsConsumer } from '@opallabs/quanta'
import Conditional from '../Conditional'
import { kuiperClassNames } from '../utils'
import Footer from './Footer'
import { defaultSearchSettings, defaultSelectedComparator } from './helpers'
import MenuContents from './MenuContents'
import { actionTypes, reducer } from './reducer'
import SearchBar from './SearchBar'
import * as styles from './styles'
import './styles.scss'

// remove onClick handler to prevent select from being called twice by tracking
const sanitizeForTracking = (menuOption) => {
  if (menuOption) {
    // eslint-disable-next-line no-unused-vars
    const { onClick, ...option } = menuOption
    return option
  }

  return menuOption
}

/**
 * @param {Object} props
 */
const Menu = ({
  avatared,
  canSelectAll,
  className,
  customFooter,
  disableKuiperStyles,
  dropdown,
  focusOnMount,
  htmlId = uniqueId('kuiper-menu'),
  isMultiSelect,
  isOpen,
  minimumSearchLength,
  noOptionsText,
  noOptionsElement,
  onChange,
  onClearAll,
  onEsc,
  onRemove,
  onSearchKeyDown,
  onSelect,
  optionDecorator,
  options,
  optionsType,
  role,
  rowHeight,
  searchable,
  searchIcon,
  searchIconSize,
  searchPlaceholderText,
  selectedComparator,
  showDeselectIcon,
  theme,
  value,
}) => {
  const menuElement = useRef(null)
  const firstSelectedOption = options.find((option) =>
    selectedComparator(option, value)
  )
  const [state, dispatch] = useReducer(reducer, {
    highlightedOption: firstSelectedOption || options[0],
    highlightedIndex: firstSelectedOption
      ? options.indexOf(firstSelectedOption)
      : 0,
    searchTerm: '',
    options,
  })

  // For use in combination with Lapidary `<Popover>`, with `false`
  // `focusOnMount`.
  useEffect(() => {
    if (typeof isOpen === 'undefined') return

    menuElement.current?.focus()
  }, [isOpen, menuElement])

  useEffect(() => {
    let timeout
    if (focusOnMount && !searchable) {
      timeout = setTimeout(() => menuElement.current.focus(), 500)
    }

    return () => clearTimeout(timeout)
  }, [focusOnMount, searchable, options])

  useEffect(() => {
    const firstSelectedOption = options.find((option) =>
      selectedComparator(option, value)
    )
    dispatch({
      type: actionTypes.SET_OPTIONS,
      data: {
        highlightedOption: firstSelectedOption || options[0],
        highlightedIndex: firstSelectedOption
          ? options.indexOf(firstSelectedOption)
          : 0,
        options,
      },
    })
  }, [options, selectedComparator, value])

  const select = (...selections) => {
    onSelect(...selections)
  }

  const clearAllSelections = () => {
    const optionsToClear = options.filter((option) =>
      selectedComparator(option, value)
    )
    if (onClearAll) return onClearAll(optionsToClear)
    return select(optionsToClear)
  }

  const selectAllOptions = () => {
    const filteredOptions = options.filter(
      (option) => !selectedComparator(option, value)
    )
    select(filteredOptions)
  }

  const resetSearchResults = () => {
    dispatch({
      type: actionTypes.SET_SEARCH,
      data: { searchTerm: '', options },
    })
  }

  const onSearch = (searchTerm) => {
    dispatch({ type: actionTypes.SET_SEARCH, data: { searchTerm, options } })
    if (searchTerm.length < minimumSearchLength) return
    const fuse = new Fuse(options, defaultSearchSettings)
    const filteredOptions = fuse.search(searchTerm).map((result) => result.item)
    dispatch({
      type: actionTypes.SET_SEARCH,
      data: { searchTerm, options: filteredOptions },
    })
  }

  const keyUp = (debouncedTrack) => (e) => {
    switch (e.keyCode) {
      case 16:
        e.preventDefault()
        dispatch({ type: actionTypes.UNSET_HOLDING_SHIFT })
        break
      default:
        debouncedTrack('menu-keydown', { value: e.target.value })
        onSearchKeyDown(e)
        break
    }
  }

  const keyDown = (debouncedTrack) => (e) => {
    switch (e.keyCode) {
      case 9:
        e.preventDefault()
        if (state.holdingShift) {
          dispatch({ type: actionTypes.SET_PREVIOUS_HIGHLIGHTED_INDEX })
          break
        }
        dispatch({ type: actionTypes.SET_NEXT_HIGHLIGHTED_INDEX })
        break
      case 16:
        e.preventDefault()
        dispatch({ type: actionTypes.SET_HOLDING_SHIFT })
        break
      case 27:
        if (onEsc) onEsc()
        debouncedTrack('menu-escape')
        break
      case 38:
        e.preventDefault()
        dispatch({ type: actionTypes.SET_PREVIOUS_HIGHLIGHTED_INDEX })
        break
      case 40:
        e.preventDefault()
        dispatch({ type: actionTypes.SET_NEXT_HIGHLIGHTED_INDEX })
        break
      case 13:
        // prevent this propagating to a V2 popover wrapper, which would
        // trigger an open event on the popover
        e.stopPropagation()
        debouncedTrack('menu-enter', {
          value: sanitizeForTracking(state.highlightedOption),
        })
        if (state.highlightedOption && !state.highlightedOption.disabled)
          select(state.highlightedOption)
        break
      default:
        debouncedTrack('menu-keydown', { value: e.target.value })
        onSearchKeyDown(e)
        break
    }
  }

  return (
    <AnalyticsConsumer>
      {({ debouncedTrack }) => (
        <div
          css={disableKuiperStyles ? styles.container({ theme }) : null}
          id={htmlId}
          ref={menuElement}
          onKeyDown={keyDown(debouncedTrack)}
          onKeyUp={keyUp(debouncedTrack)}
          className={kuiperClassNames(
            disableKuiperStyles || 'menu',
            className,
            {
              dropdown,
            }
          )}
          role={role}
          aria-live="polite"
          aria-atomic="true"
          aria-relevant="all"
          tabIndex={-1}
        >
          <Conditional condition={searchable}>
            <SearchBar
              focusOnMount={focusOnMount}
              icon={searchIcon}
              iconSize={searchIconSize}
              onBlur={() =>
                // this setTimeout allows popovers to
                // fade away before resetting the list
                setTimeout(() => resetSearchResults(), 175)
              }
              onSearch={onSearch}
              placeholderText={searchPlaceholderText}
              value={state.searchTerm}
            />
          </Conditional>

          <MenuContents
            avatared={avatared}
            disableKuiperStyles={disableKuiperStyles}
            highlightedIndex={state.highlightedIndex}
            highlightedOption={state.highlightedOption}
            isMultiSelect={isMultiSelect}
            noOptionsText={noOptionsText}
            noOptionsElement={noOptionsElement}
            onChange={onChange}
            onHoverOption={() =>
              dispatch({ type: actionTypes.CLEAR_HIGHLIGHT })
            }
            onRemove={onRemove}
            onSelect={select}
            optionDecorator={optionDecorator}
            options={state.options}
            optionsType={optionsType}
            role={role}
            rowHeight={rowHeight}
            selectedComparator={selectedComparator}
            selectedOptions={value}
            showDeselectIcon={showDeselectIcon}
            theme={theme}
          />

          <Conditional
            condition={
              isMultiSelect &&
              !state.searchTerm &&
              (state.options.length > 0 || value.length > 0)
            }
          >
            <Footer
              canClear={!!value.length}
              canSelectAll={canSelectAll && state.options.length > 0}
              clearAll={clearAllSelections}
              selectAll={selectAllOptions}
            />
          </Conditional>
          <Conditional condition={Boolean(customFooter)}>
            {customFooter}
          </Conditional>
        </div>
      )}
    </AnalyticsConsumer>
  )
}
Menu.displayName = 'Kuiper: Menu'

Menu.defaultProps = {
  canSelectAll: true,
  disableKuiperStyles: false,
  dropdown: false,
  focusOnMount: false,
  minimumSearchLength: 2,
  noOptionsText: 'No options available.',
  onSearchKeyDown: () => {},
  onSelect: () => {},
  role: 'menu',
  rowHeight: 27,
  selectedComparator: defaultSelectedComparator,
  searchIcon: 'search',
  searchIconSize: 16,
  searchPlaceholderText: 'Search...',
  theme: 'light',
  value: [],
}

Menu.propTypes = {
  avatared: bool,
  canSelectAll: bool,
  className: string,
  customFooter: object,
  disableKuiperStyles: bool,
  dropdown: bool,
  focusOnMount: bool,
  htmlId: string,
  isMultiSelect: bool,
  isOpen: bool,
  minimumSearchLength: number,
  noOptionsText: string,
  noOptionsElement: object,
  onChange: func,
  onClearAll: func,
  onEsc: func,
  onRemove: func,
  onSearchKeyDown: func,
  onSelect: func,
  optionDecorator: func,
  options: array,
  optionsType: string,
  role: oneOf(['listbox', 'menu']),
  rowHeight: number,
  searchable: bool,
  searchIcon: string,
  searchIconSize: number,
  searchPlaceholderText: string,
  selectedComparator: func,
  showDeselectIcon: bool,
  theme: oneOf(['dark', 'light']),
  value: oneOfType([array, object]),
}

export default Menu
